import { useLazyQuery } from '@apollo/client/react';
import { PipelineStatusBadge } from '@optools/components/pipelines/pipeline-status-badge';
import { TeamSpot, TeamSpotProps } from '@optools/components/team-spot';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { cn } from '@optools/components/ui/utils';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import { BtcSchedulePipeline } from '@optools/services/operator-gateway/btc/types';
import {
  PipelineEviStatus,
  PipelineStatus,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { getMediaId } from '@optools/utils/pipeline';
import { Link, useSearch } from '@tanstack/react-router';
import { Cable, CircleAlert, Projector, RefreshCcw, Zap } from 'lucide-react';

export interface PipelineCardProps {
  gamePk: number;
  pipeline: BtcSchedulePipeline;
  showLink?: boolean;
  team?: TeamSpotProps['team'];
}

export function PipelineCard({ gamePk, pipeline, showLink = true, team }: PipelineCardProps) {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule' });
  const mediaId = getMediaId(pipeline);

  const [refreshPipelines, { called, loading, refetch }] = useLazyQuery(BTC_GAME_PIPELINE, {
    fetchPolicy: 'network-only',
  });

  const { alerts, eviPod, eviStatus, programSource } = pipeline;

  const activeAlertCount = alerts?.filter(({ active }) => active).length || 0;
  const hasActiveAlerts = activeAlertCount > 0;

  return (
    <Card
      className={cn('group relative isolate flex grow justify-between p-4', {
        'mt-4 border-red-500': hasActiveAlerts,
      })}
      data-testid={`game-pipeline-${pipeline.id}-${mediaId}`}>
      {showLink && (
        <Link
          aria-label="Pipeline Detail"
          className={cn(
            'peer absolute inset-0 -z-10 rounded-xl transition-colors hover:bg-accent focus:bg-accent',
            {
              'bg-red-300/5 hover:bg-red-300/10 focus:bg-red-300/10': hasActiveAlerts,
            },
          )}
          from="/"
          params={{ feedType: pipeline.feedType, gamePk }}
          search={{ date }}
          to="/btc-tools/schedule/$gamePk/pipelines/$feedType"
        />
      )}

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <TeamSpot showAbbreviation={false} size={32} team={team} />
          <h1 className="text-sm font-semibold">{pipeline.feedType}</h1>
        </div>
        {pipeline?.regionalPipelines?.length > 0 ? (
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              <Zap />
              {pipeline.streamState}
            </Badge>
            <div className="grid grid-cols-[auto_auto_auto] items-center gap-x-3 gap-y-2">
              {pipeline.regionalPipelines.map(({ activeSource, id, region, status }) => {
                return (
                  <div key={id} className="contents" data-testid={`${region}-pipeline-${id}`}>
                    <h2 className="text-sm font-semibold">{region}</h2>
                    <PipelineStatusBadge status={status} />
                    <Badge>
                      <Cable />
                      {activeSource}
                    </Badge>
                  </div>
                );
              })}
            </div>
            {eviStatus === PipelineEviStatus.ENABLED && (
              <Badge>
                <Projector />
                EVI Enabled
                {eviPod?.name ? (
                  <>
                    :<span className="font-bold">{eviPod?.name}</span>
                  </>
                ) : null}
              </Badge>
            )}
          </div>
        ) : (
          <div className="flex gap-2">
            <PipelineStatusBadge status={pipeline.status} />
            <Badge variant="outline">
              <Zap />
              {pipeline.streamState}
            </Badge>
            <Badge>
              <Cable />
              {programSource}
            </Badge>
            {eviStatus === PipelineEviStatus.ENABLED && (
              <Badge>
                <Projector />
                EVI Enabled
                {eviPod?.name ? (
                  <>
                    :<span className="font-bold">{eviPod?.name}</span>
                  </>
                ) : null}
              </Badge>
            )}
          </div>
        )}
      </div>

      {pipeline.status !== PipelineStatus.ARCHIVED ? (
        <Button
          className="peer self-center"
          data-testid="refresh-pipeline"
          disabled={loading}
          size="icon"
          title="Refresh Pipeline"
          variant="outline"
          onClick={() =>
            void (called
              ? refetch()
              : refreshPipelines({ variables: { gamePk, scheduleDate: date } }))
          }>
          <RefreshCcw />
        </Button>
      ) : null}

      {hasActiveAlerts && (
        <div className="absolute top-0 left-0 z-10 flex origin-top-left -translate-x-3 -translate-y-1/2 items-center justify-items-end gap-1">
          <span
            className="flex items-center justify-center rounded-full bg-(--pipelines-container-color) p-0.5 text-red-500 transition-colors"
            data-testid="pipeline-alerts-indicator">
            <CircleAlert className="size-5" />
          </span>

          <Badge className="border-red-500 bg-(--pipelines-container-color) px-2 py-0 text-xs font-semibold text-red-500 transition-colors">
            {activeAlertCount} alert{activeAlertCount > 1 ? 's' : ''}
          </Badge>
        </div>
      )}
    </Card>
  );
}
